/**
 * Every event, as a sentence.
 *
 * The log is what tells a player what the others did while they were thinking,
 * so an event the log cannot describe is a hole in the game rather than a
 * cosmetic gap. The switch below is exhaustive over `GameEvent`: adding an
 * event kind to the engine without a sentence for it stops the build, and
 * `narrate.test.ts` checks every kind a real game reaches produces a sentence
 * that names the player and says what happened.
 */
import { MANIFESTS, type GameEvent, type PlayerId } from '@trajan/engine';
import { cardType, describeCards } from '../cards/cards';

export interface Line {
  /** The event's own index in the log; stable, and unique. */
  id: number;
  /** Whose doing it was, when it was anyone's. */
  player: PlayerId | null;
  text: string;
  /** Quarter and round boundaries, scoring, and game over stand out. */
  weight: 'normal' | 'loud';
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const tray = (i: number) => MANIFESTS.trayActions[i] ?? `tray ${i + 1}`;
const vp = (n: number) => `${n} VP`;

/** One line of the log. `who` turns a seat into the name the reader knows. */
export function narrate(event: GameEvent, id: number, who: (seat: PlayerId) => string): Line {
  const line = (
    player: PlayerId | null,
    text: string,
    weight: Line['weight'] = 'normal',
  ): Line => ({
    id,
    player,
    text,
    weight,
  });

  switch (event.type) {
    case 'MarkersArranged':
      return line(event.player, `${who(event.player)} arranged their markers.`);
    case 'InitialTrajanTilesChosen':
      return line(
        event.player,
        `${who(event.player)} took three Trajan tiles, onto slots ${event.slots
          .map((s) => ROMAN[s])
          .join(', ')}.`,
      );
    case 'GameStarted':
      return line(null, `The game begins. ${who(event.first)} goes first.`, 'loud');
    case 'TurnStarted':
      return line(event.player, `${who(event.player)}'s turn.`);

    case 'TrayMoved':
      return line(
        event.player,
        `${who(event.player)} picked up the ${tray(event.source)} tray and dropped ` +
          `${plural(event.moved, 'marker')} round to ${tray(event.target)}.`,
      );
    case 'TimeAdvanced':
      return line(
        null,
        `Time moves ${plural(event.by, 'space')} to ${event.to}` +
          (event.crossedStart ? ', past the start space.' : '.'),
      );

    case 'TrajanTileOffered':
      // A shared log needs to say whose mat, not just which slot.
      return line(
        event.player,
        `${who(event.player)}'s tile in slot ${ROMAN[event.slot]} is satisfied.`,
      );
    case 'TrajanTileDeclined':
      return line(event.player, `${who(event.player)} left the slot ${ROMAN[event.slot]} tile.`);
    case 'TrajanTileAccomplished':
      return line(
        event.player,
        `${who(event.player)} accomplished the slot ${ROMAN[event.slot]} tile for ${vp(event.vp)}.`,
      );
    case 'TrajanTileTaken':
      return line(
        event.player,
        `${who(event.player)} took a Trajan tile onto slot ${ROMAN[event.slot]}.`,
      );
    case 'ArchMoved':
      return line(
        event.player,
        event.to === 'center'
          ? `${who(event.player)}'s Arch has nowhere to go and waits in the middle.`
          : `${who(event.player)}'s Arch moved to slot ${ROMAN[event.to]}.`,
      );

    case 'CardsDrawn':
      return line(event.player, `${who(event.player)} drew ${plural(event.count, 'card')}.`);
    case 'DrawPileReshuffled':
      return line(null, `The discards were shuffled into a new draw pile of ${event.cards}.`);
    // Every card below is face up by the time it is narrated — discarded onto a
    // pile, put on display, or shipped — so naming it tells nobody anything the
    // table does not already show, and "shipped 3 cards" told them nothing.
    case 'CardDiscarded':
      return line(
        event.player,
        `${who(event.player)} discarded ${anIcon(cardType(event.cardId))} onto pile ${event.pile + 1}.`,
      );
    case 'CardTakenFromDiscard':
      return line(
        event.player,
        `${who(event.player)} took the ${cardType(event.cardId)} from pile ${event.pile + 1}.`,
      );
    case 'DiscardPileRefilled':
      return line(
        null,
        `Pile ${event.pile + 1} was refilled from the draw pile: ${anIcon(cardType(event.cardId))}.`,
      );
    case 'CardsDisplayed':
      return line(
        event.player,
        `${who(event.player)} put ${describeCards(event.cardIds)} on display.`,
      );
    case 'CardsShipped':
      return line(
        event.player,
        `${who(event.player)} shipped ${describeCards(event.cardIds)}` +
          (event.wildTileIds.length > 0
            ? ` with ${plural(event.wildTileIds.length, 'wild tile')}`
            : '') +
          ` for ${vp(event.vp)}.`,
      );

    case 'TokensDeployed':
      return line(
        event.player,
        `${who(event.player)} deployed ${plural(
          event.count,
          event.camp === 'workers' ? 'worker' : 'legionnaire',
        )} to the camp.`,
      );
    case 'LeaderMoved':
      return line(
        event.player,
        `${who(event.player)}'s leader moved to ${province(event.to)}` +
          (event.seizedTileId ? ', seizing the tile there.' : '.'),
      );
    case 'ProvinceOccupied':
      return line(
        event.player,
        `${who(event.player)} occupied ${province(event.province)} for ${vp(event.vp)}.`,
      );

    case 'ForumTileTaken':
      return line(
        event.player,
        `${who(event.player)} took ${anIcon(forumTileName(event.tileId, event.kind))} tile.`,
      );
    case 'ExtraActionTileTaken':
      return line(event.player, `${who(event.player)} took a ${event.action} extra-action tile.`);
    case 'SenateAdvanced':
      return line(
        event.player,
        `${who(event.player)} moved up the senate track to ${event.to} for ${vp(event.vp)}.`,
      );
    case 'Plus2MarkerPlaced':
      return line(
        event.player,
        `${who(event.player)} put a “+2” marker on their ${event.space} space.`,
      );
    case 'ExtraActionUsed':
      return line(
        event.player,
        `${who(event.player)} spent a tile for ${event.times === 1 ? 'another' : `${event.times} more`} ${event.action} ${event.times === 1 ? 'action' : 'actions'}.`,
      );

    case 'WorkerBuilt':
      return line(
        event.player,
        event.tileId
          ? `${who(event.player)} built ${anIcon(constructionIcon(event.tileId))} for ${vp(event.vp)}.`
          : `${who(event.player)} built on a site with no tile left to take.`,
      );
    case 'ConstructionBonus':
      return line(
        event.player,
        `${who(event.player)}'s first ${event.icon} earns a free ${event.action} action.`,
      );

    case 'VpGained':
      return line(event.player, `${who(event.player)} scored ${vp(event.vp)} for ${event.reason}.`);
    case 'ActionSkipped':
      return line(event.player, `${who(event.player)} skipped the ${event.action} action.`);
    case 'TurnEnded':
      return line(event.player, `${who(event.player)} ended their turn.`);
    case 'RoundEnded':
      return line(null, `End of round ${event.round}, quarter ${event.quarter}.`, 'loud');

    case 'DemandRevealed':
      return line(null, `The people demand ${event.icon}.`, 'loud');
    case 'QuarterEnded':
      return line(null, `Quarter ${event.quarter} is over.`, 'loud');
    case 'DemandMet':
      return line(
        event.player,
        `${who(event.player)} met the ${event.icon} demand` +
          (event.source === 'wild'
            ? ' with a wild tile.'
            : event.source === 'trajan'
              ? ' with a tile from their mat.'
              : ' with a forum tile.'),
      );
    case 'DemandUnmet':
      return line(event.player, `${who(event.player)} could not meet the ${event.icon} demand.`);
    case 'DemandPenalty':
      return line(
        event.player,
        `${who(event.player)} missed ${plural(event.misses, 'demand')}: ${vp(event.vp)}.`,
        'loud',
      );
    case 'SenateBalance':
      return line(
        null,
        `Balance of power: ${who(event.consul)} is consul, ${who(event.viceConsul)} vice-consul.`,
        'loud',
      );
    case 'BonusTileTaken':
      return line(event.player, `${who(event.player)} took a bonus tile, ${event.side} side up.`);
    case 'SenateReset':
      return line(
        null,
        `The senate resets, with ${who(event.stack[event.stack.length - 1] ?? 0)} on top of the stack.`,
      );
    case 'QuarterRefilled':
      return line(null, `The board is restocked for quarter ${event.quarter + 1}.`);

    case 'FinalScoring':
      // The breakdown is what the end of the game added, not the final total.
      return line(
        event.player,
        `${who(event.player)} gains ${vp(event.breakdown.total)} in the final scoring.`,
        'loud',
      );
    case 'GameOver':
      return line(
        null,
        `Game over. ${event.winners.map(who).join(' and ')} ${event.winners.length > 1 ? 'share the win' : 'wins'}.`,
        'loud',
      );
  }
}

const province = (id: string) => MANIFESTS.provinces.find((p) => p.id === id)?.name ?? id;

/** "a stair", "an aqueduct" — the article the icon's own name wants. */
const anIcon = (name: string) => `${/^[aeiou]/i.test(name) ? 'an' : 'a'} ${name}`;

const constructionIcon = (tileId: string) =>
  MANIFESTS.tiles.construction.find((t) => t.id === tileId)?.icon ?? 'tile';

/**
 * A demand tile is worth naming by what it demands; the rest say what they do,
 * and the hyphens in the engine's kind are not for reading.
 */
function forumTileName(tileId: string, kind: string): string {
  const tile = MANIFESTS.tiles.forum.find((t) => t.id === tileId);
  return tile && tile.kind === 'demand' && tile.icon ? tile.icon : kind.replace(/-/g, ' ');
}

/** The whole log, newest last, as the panel renders it. */
export function narrateAll(events: readonly GameEvent[], who: (seat: PlayerId) => string): Line[] {
  return events.map((e, i) => narrate(e, i, who));
}
